import { useNavigate } from "@tanstack/react-router"
import React from "react"
import { z } from "zod"

export const PaginationSearchSchema = z.object({
  // Coerce arrays (from duplicate URL params like ?page=1&page=2) and strings
  // to a number, falling back to 1 on any invalid input.
  page: z
    .preprocess(
      (val) => (Array.isArray(val) ? val[0] : val),
      z.coerce.number().positive().int().catch(1).default(1),
    )
    .default(1),
})

type PaginationSearch = {
  page: number
  query?: string
  filterBy?: unknown
  field?: string
  order?: "asc" | "desc"
}

type PaginationQueryProps = {
  search: PaginationSearch
  fullPath: string
  total: number
}

export function usePagination({ search, fullPath, total }: PaginationQueryProps) {
  const [count, setCount] = React.useState<number | undefined>()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigate = useNavigate({ from: fullPath as any })
  const { page } = search
  const comparableSearch = React.useMemo(
    () => ({
      query: search.query,
      filterBy: search.filterBy,
      field: search.field,
      order: search.order,
    }),
    [search.field, search.filterBy, search.order, search.query],
  )
  const previousComparableSearch = React.useRef(comparableSearch)

  const setPage = React.useCallback(
    (page: number) => {
      navigate({
        // TODO: Remove this once the type is fixed
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        search: { ...search, page } as any,
      })
    },
    [navigate, search],
  )

  React.useEffect(() => {
    const hasChanged =
      JSON.stringify(comparableSearch) !== JSON.stringify(previousComparableSearch.current)
    previousComparableSearch.current = comparableSearch
    if (hasChanged && page > 1) {
      setPage(1)
    }
  }, [comparableSearch, page, setPage])

  const totalPages = count ? Math.ceil(count / total) : undefined

  return {
    page,
    totalPages,
    setCount,
    setPage,
    count,
  }
}
